import { attr, customElement, GenesisElement, observable, volatile } from '@genesislcap/web-core';
import { formatRatingFilterLabel } from '../deals.util';
import { CombinedRatingsStyles as styles } from './combined-ratings.styles';
import { CombinedRatingsTemplate as template } from './combined-ratings.template';

export const COMBINED_RATINGS_CHANGE_EVENT = 'combined-ratings-change';

export interface CombinedRatingsChangeDetail {
  selectedValues: string[];
}

@customElement({
  name: 'combined-ratings',
  template,
  styles,
  // ponytail: light DOM so AG Grid theme classes apply (shadow root blocks ag-set-filter styles)
  shadowOptions: null,
})
export class CombinedRatings extends GenesisElement {
  @observable options: string[] = [];
  @observable selectedValues: string[] = [];
  @observable searchText = '';
  @attr({ attribute: 'search-placeholder' }) searchPlaceholder = 'Search...';

  selectAllInput!: HTMLInputElement;

  optionsChanged(): void {
    const selectedSet = new Set(this.selectedValues?.length ? this.selectedValues : this.options);
    this.selectedValues = this.options.filter((option) => selectedSet.has(option));
  }

  selectedValuesChanged(): void {
    this.syncSelectAllInput();
  }

  @volatile get visibleOptions(): string[] {
    const normalizedSearch = this.searchText.trim().toLowerCase();
    if (!normalizedSearch) {
      return this.options;
    }
    return this.options.filter(
      (option) =>
        option.toLowerCase().includes(normalizedSearch) ||
        formatRatingFilterLabel(option).toLowerCase().includes(normalizedSearch),
    );
  }

  @volatile get selectAllState(): { checked: boolean; indeterminate: boolean } {
    const total = this.options.length;
    const selected = this.selectedValues.length;
    if (selected === 0) {
      return { checked: false, indeterminate: false };
    }
    if (selected === total) {
      return { checked: true, indeterminate: false };
    }
    return { checked: false, indeterminate: true };
  }

  @volatile get selectAllWrapperClass(): string {
    const { checked, indeterminate } = this.selectAllState;
    return buildCheckboxWrapperClass(checked, indeterminate);
  }

  formatOptionLabel(option: string): string {
    return formatRatingFilterLabel(option);
  }

  getOptionWrapperClass(option: string): string {
    return buildCheckboxWrapperClass(this.selectedValues.includes(option), false);
  }

  clearSearch(): void {
    this.searchText = '';
  }

  handleSearchInput(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value ?? '';
  }

  handleSelectAllChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedValues = checked ? [...this.options] : [];
    this.emitSelectionChange();
  }

  handleSelectAllLabelClick(): void {
    const shouldSelectAll = !this.selectAllState.checked || this.selectAllState.indeterminate;
    this.selectedValues = shouldSelectAll ? [...this.options] : [];
    this.emitSelectionChange();
  }

  handleOptionChange(event: Event, optionLabel: string): void {
    const checked = (event.target as HTMLInputElement).checked;
    const selectedSet = new Set(this.selectedValues);
    if (checked) {
      selectedSet.add(optionLabel);
    } else {
      selectedSet.delete(optionLabel);
    }
    this.selectedValues = this.options.filter((option) => selectedSet.has(option));
    this.emitSelectionChange();
  }

  handleOptionLabelClick(optionLabel: string): void {
    const selectedSet = new Set(this.selectedValues);
    if (selectedSet.has(optionLabel)) {
      selectedSet.delete(optionLabel);
    } else {
      selectedSet.add(optionLabel);
    }
    this.selectedValues = this.options.filter((option) => selectedSet.has(option));
    this.emitSelectionChange();
  }

  selectAllInputChanged(): void {
    this.syncSelectAllInput();
  }

  private syncSelectAllInput(): void {
    if (this.selectAllInput) {
      this.selectAllInput.indeterminate = this.selectAllState.indeterminate;
    }
  }

  private emitSelectionChange(): void {
    const detail: CombinedRatingsChangeDetail = {
      selectedValues: this.selectedValues,
    };
    this.$emit(COMBINED_RATINGS_CHANGE_EVENT, detail);
  }
}

function buildCheckboxWrapperClass(checked: boolean, indeterminate: boolean): string {
  return [
    'ag-wrapper',
    'ag-input-wrapper',
    'ag-checkbox-input-wrapper',
    checked ? 'ag-checked' : '',
    indeterminate ? 'ag-indeterminate' : '',
  ]
    .filter(Boolean)
    .join(' ');
}
